import ValueFormat = App.Enums.ValueFormat;
import {
	defaultNumberFormatter,
	numberNoFractionFormatter,
	percentageFormatter,
	moneyFormatter,
	moneyNoFractionFormatter,
	hourFormatter,
	minuteFormatter,
} from "./scaleNumberFormatPresets.js";
import LabelScaleFormat = App.Enums.LabelScaleFormat;

function formatNumberInternal(
	value: number,
	type: ValueFormat = "default",
	options?: Intl.NumberFormatOptions,
	locale?: string
): string {
	if (type === "percentage") {
		if (value === null) {
			value = Infinity;
		}
		return percentageFormatter(options, locale).format(value);
	}
	if (type === "money") {
		return moneyFormatter(options, locale).format(value).replace(/€\s+/, "€");
	}
	if (type === "money_no_fraction") {
		return moneyNoFractionFormatter(options, locale).format(value).replace(/€\s+/, "€");
	}
	if (type === "number_no_fraction") {
		return numberNoFractionFormatter(options, locale).format(value);
	}
	if (type === "rate_card") {
		if (typeof value === "string") {
			value = parseInt(value);
		}

		// To prevent ratecards from displaying random ASCII characters, return an empty string if they're outside A-Z
		if (value < 1 || value > 26) {
			return "";
		}
		return String.fromCharCode(value + 64);
	}

	return defaultNumberFormatter(options, locale).format(value);
}

export function formatNumberAsPercentageShare(value: number): string {
	return formatNumber(value / 100, "percentage");
}

/**
 * `locale` overrides the one set at boot. Only needed by a caller that already
 * holds a locale of its own and must round-trip against it — see
 * LocaleNumberInput, which parses with the environment's locale and so cannot
 * format with a different one.
 */
export function formatNumber(
	value: number,
	type: ValueFormat = "default",
	options?: Intl.NumberFormatOptions,
	locale?: string
): string {
	if (type === "power") {
		return formatNumberInternal(value / 1000, "default", undefined, locale) + "kW";
	}
	if (type === "energy") {
		return formatNumberInternal(value / 1000, "default", undefined, locale) + "kWh";
	}
	if (type === "time_hour_minute") {
		const hours = Math.floor(value / 60);
		const minutes = value % 60;

		if (hours > 0) {
			return `${hourFormatter(options, locale).format(hours)} ${minuteFormatter(
				options,
				locale
			).format(minutes)}`;
		}

		return `${minuteFormatter(options, locale).format(minutes)}`;
	}

	return formatNumberInternal(value, type, options, locale);
}

function parseCurrency(value: string): number {
	return parseFloat(value.replace(/[^0-9.-]+/g, ""));
}

export function parseFormattedNumber(
	formattedValue: string,
	type: ValueFormat = "default"
): number {
	if (type === "percentage") {
		return parseFloat(formattedValue.replace("%", "")) / 100;
	}
	if (type === "money" || type === "money_no_fraction") {
		return parseCurrency(formattedValue);
	}
	if (type === "number_no_fraction") {
		return parseInt(formattedValue);
	}

	return parseFloat(formattedValue);
}

export type ScaleResolution = {
	atLeast: number;
	suffix: string;
};

const numberResolutions: ScaleResolution[] = [
	{ atLeast: 1000_000_000, suffix: "B" },
	{ atLeast: 1000_000, suffix: "M" },
	{ atLeast: 1000, suffix: "K" },
	{ atLeast: 0, suffix: "" },
];

const numberResolutionsPower: ScaleResolution[] = [
	{ atLeast: 1000_000_000, suffix: "GW" },
	{ atLeast: 1000_000, suffix: "MW" },
	{ atLeast: 1000, suffix: "kW" },
	{ atLeast: 0, suffix: "W" },
];

const numberResolutionsEnergy: ScaleResolution[] = [
	{ atLeast: 1000_000_000, suffix: "GWh" },
	{ atLeast: 1000_000, suffix: "MWh" },
	{ atLeast: 1000, suffix: "kWh" },
	{ atLeast: 0, suffix: "Wh" },
];

function getResolutionsForFormat(format: ValueFormat): ScaleResolution[] {
	if (format === "percentage") {
		return [];
	}
	if (format === "power") {
		return numberResolutionsPower;
	}
	if (format === "energy") {
		return numberResolutionsEnergy;
	}

	return numberResolutions;
}

/**
 * How many decimals a value already divided down to its scale should show.
 *
 * Set explicitly rather than left to the format's own defaults: money asks for
 * two, so a whole scaled value fell through to "€43.00M" while a fractional
 * one took the branch below and read "€42.5M". Both min and max, because Intl
 * throws when a minimum it was given outranks a maximum it inherited.
 *
 * The decision is made on the value *as it will be displayed*, not as it is
 * held. Asking whether the raw number is whole makes the answer turn on
 * differences far below the decimal being shown: 50,000,000 reads "€50M" while
 * 49,999,999.97 — the same figure summed a different way — reads "€50,0M". Two
 * quantities that are equal to every digit on screen then render differently,
 * which breaks anything comparing what the user is actually looking at.
 */
function scaledFractionOptions(value: number): Intl.NumberFormatOptions {
	const asDisplayed = Math.round(value * 10) / 10;
	const showsFraction = value < 100 && !Number.isInteger(asDisplayed);

	return showsFraction
		? { maximumFractionDigits: 1, minimumFractionDigits: 1 }
		: { maximumFractionDigits: 0, minimumFractionDigits: 0 };
}

export function formatNumberForScale(
	format: ValueFormat,
	value: number,
	tickSpacing?: number
): string {
	// use the absolute value of tickSpacing to work with the resolutions
	tickSpacing = Math.abs(tickSpacing ?? value);

	const resolution = getResolutionsForFormat(format).find(
		(nr) => tickSpacing >= nr.atLeast
	);

	if (value === 0) {
		return formatNumber(value, format);
	}

	if (resolution?.atLeast) {
		value /= resolution.atLeast;

		return (
			formatNumberInternal(value, format, scaledFractionOptions(value)) +
			resolution?.suffix
		);
	}

	return formatNumber(value, format);
}

function formatWithResolution(
	format: ValueFormat,
	value: number,
	resolution: ScaleResolution
): string {
	if (resolution?.atLeast) {
		if (format === "money") {
			format = "money_no_fraction";
		} else if (format === "default") {
			format = "number_no_fraction";
		}
	}

	if (resolution?.atLeast) {
		value /= resolution.atLeast;

		return (
			formatNumberInternal(value, format, scaledFractionOptions(value)) +
			resolution.suffix
		);
	}

	return formatNumber(value, format);
}

export function getScaleResolutionsForBar(
	format: ValueFormat,
	value: number,
	barWidth?: number
): ScaleResolution[] {
	if (barWidth) {
		barWidth = (barWidth - 5) / 8; // max px to max chars
	}

	let resolutions = getResolutionsForFormat(format);
	resolutions = resolutions.filter((r) => Math.abs(value) >= r.atLeast);
	if (!resolutions.length) {
		return [];
	}

	const fittingResolutions =
		barWidth === undefined
			? resolutions
			: resolutions.filter(
					(r) => formatWithResolution(format, value, r).length < barWidth
			  );
	if (!fittingResolutions.length) {
		return [resolutions[0]];
	}

	const matchingResolutions = fittingResolutions.filter(
		(r) => formatWithResolution(format, value, r).length < barWidth
	);
	if (!matchingResolutions.length) {
		return [fittingResolutions[0]];
	}

	return matchingResolutions;
}

export function formatNumberForBar(
	format: ValueFormat,
	scaleFormat: LabelScaleFormat,
	value: number,
	barWidth?: number
): string {
	if (!Number.isFinite(value)) return "";

	let resolution: ScaleResolution | undefined;
	const canUseResolution = format !== "rate_card" && format !== "percentage";

	if (canUseResolution) {
		switch (scaleFormat) {
			case "auto": {
				const resolutions = getScaleResolutionsForBar(format, value, barWidth);
				resolution = resolutions?.[resolutions.length - 1];
				break;
			}
			case "thousands": {
				resolution = getResolutionsForFormat(format).find(
					(r) => r.atLeast === 1000
				);
				break;
			}
			case "millions": {
				resolution = getResolutionsForFormat(format).find(
					(r) => r.atLeast === 1000_000
				);
				break;
			}
		}
	}

	if (value === 0) {
		return formatNumber(value, format);
	}

	if (resolution) {
		return formatWithResolution(format, value, resolution);
	}

	return formatNumber(value, format);
}
